import { useEffect, useState } from "react";
import HomePanel from "./pages/HomePanel";
import TelemetryPanel from "./pages/TelemetryPanel";
import EventsPanel from "./pages/EventsPanel";
import ReportsPanel from "./pages/ReportsPanel";
import AdminPanel from "./pages/AdminPanel";
import DesktopHint, { isInsideTelegram } from "./components/DesktopHint";
import LanguageSwitcher from "./components/LanguageSwitcher";
import { api, ApiError } from "./api";
import { useT, normalizeLang } from "./i18n";
import { tg } from "./telegram";
import { AppProvider, useApp, type Tab } from "./state/app";
import type { MeOut } from "./types";

// Five tabs by task: 🏠 Главная · 📈 Телеметрия · 🚨 События · 📄 Отчёты · ⚙️ Управление.
// Panels are gated per user by me.allowed_tabs ("auto" = Счётчик, "scada_v2" =
// G1/G2 — both inside «Телеметрия»; "combined" / "incidents" → «События»).
// Admins see everything + «Управление».
export default function App() {
  const { t, lang, setLang } = useT();
  const inside = isInsideTelegram();
  const [me, setMe] = useState<MeOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!inside) return;
    api.get<MeOut>("/api/me")
      .then((m) => {
        setMe(m);
        const serverLang = normalizeLang(m.language_code);
        if (serverLang !== lang) setLang(serverLang);
      })
      .catch((e: ApiError) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inside]);

  if (!inside) return <DesktopHint />;
  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h2>{error}</h2>
        <p style={{ color: "var(--hint)", fontSize: 13 }}>{t("access.locked_hint")}</p>
      </div>
    );
  }
  if (!me) return <div className="empty-state" style={{ padding: 80 }}><span className="spinner" /></div>;

  const allowed = me.allowed_tabs?.length ? me.allowed_tabs : ["auto", "scada_v2"];
  const hasUnits = allowed.includes("scada_v2");
  const hasPa = allowed.includes("auto");
  return (
    <AppProvider initialSource={hasUnits ? "g1" : "pa"}>
      <Shell me={me} hasUnits={hasUnits} hasPa={hasPa}
        hasCombined={allowed.includes("combined")} hasIncidents={allowed.includes("incidents")} />
    </AppProvider>
  );
}

function Shell({ me, hasUnits, hasPa, hasCombined, hasIncidents }: {
  me: MeOut; hasUnits: boolean; hasPa: boolean; hasCombined: boolean; hasIncidents: boolean;
}) {
  const { t } = useT();
  const { tab, setTab } = useApp();
  const isAdmin = me.role === "admin";
  const hasEvents = hasUnits || hasCombined || hasIncidents;
  const tabs: { key: Tab; icon: string; label: string }[] = [
    { key: "home", icon: "🏠", label: t("tabs.home") },
    ...(hasUnits || hasPa ? [{ key: "telemetry" as Tab, icon: "📈", label: t("tabs.telemetry") }] : []),
    ...(hasEvents ? [{ key: "events" as Tab, icon: "🚨", label: t("tabs.events") }] : []),
    ...(hasUnits || (isAdmin && hasPa) ? [{ key: "reports" as Tab, icon: "📄", label: t("tabs.reports") }] : []),
    ...(isAdmin ? [{ key: "admin" as Tab, icon: "⚙️", label: t("tabs.admin") }] : []),
  ];
  const effTab: Tab = tabs.some((x) => x.key === tab) ? tab : "home";
  const panel =
    effTab === "admin" ? <AdminPanel />
      : effTab === "reports" ? <ReportsPanel isAdmin={isAdmin} hasUnits={hasUnits} hasPa={hasPa} />
      : effTab === "events" ? <EventsPanel hasUnits={hasUnits} hasIncidents={hasIncidents} hasCombined={hasCombined} />
        : effTab === "telemetry" ? <TelemetryPanel hasUnits={hasUnits} hasPa={hasPa} />
          : <HomePanel hasUnits={hasUnits} hasPa={hasPa} hasEvents={hasEvents} />;

  return (
    <div className="app">
      <LanguageSwitcher />
      <main className="main">{panel}</main>
      <nav className="tabbar">
        {tabs.map((tb) => (
          <button key={tb.key} className={"tab" + (effTab === tb.key ? " active" : "")}
            onClick={() => { tg()?.HapticFeedback?.selectionChanged?.(); setTab(tb.key); }}>
            <span className="tab-icon">{tb.icon}</span><span>{tb.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
